import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Space, Typography, Spin } from 'antd';
import { Html5Qrcode } from 'html5-qrcode';

const { Title, Text } = Typography;

interface QrScannerProps {
  onScanSuccess: (decodedText: string) => void;
  onScanError: (errorMessage: string) => void;
  onCancel?: () => void;
}

const QrScanner: React.FC<QrScannerProps> = ({ onScanSuccess, onScanError, onCancel }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Función para detener y limpiar instancia y contenedor
  const cleanupScanner = async () => {
    if (scannerRef.current) {
      if (scannerRef.current.isScanning) {
        await scannerRef.current.stop().catch(() => {});
      }
      try {
        scannerRef.current.clear();
      } catch {
        // Intentionally swallowing errors
      }
      scannerRef.current = null;
    }
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
  };

  useEffect(() => {
    return () => {
      // Al desmontar, limpia todo
      cleanupScanner().catch(() => {});
    };
  }, []);

  const initializeScanner = async () => {
    setIsLoading(true);
    setErrorMsg(null);

    // Llamar al garbage collector antes de iniciar
    await cleanupScanner();

    try {
      const scannerId = "qr-reader-" + Math.random().toString(36).substring(2, 9);
      
      // Create scanner container if not already present
      if (containerRef.current) {
        // Remove any existing scanner elements
        while (containerRef.current.firstChild) {
          containerRef.current.removeChild(containerRef.current.firstChild);
        }
        
        // Create a new scanner element with unique ID
        const scannerElement = document.createElement('div');
        scannerElement.id = scannerId;
        scannerElement.style.width = '100%';
        scannerElement.style.minHeight = '300px';
        containerRef.current.appendChild(scannerElement);
        
        // Initialize scanner with newly created element
        scannerRef.current = new Html5Qrcode(scannerId);
      }
    } catch (err) {
      setErrorMsg(`Error al inicializar escáner: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const startScanner = async () => {
    setErrorMsg(null);
    setIsLoading(true);
    try {
      // fully clean previous instance
      await cleanupScanner();
      // init new scanner container and instance
      await initializeScanner();
      // start camera capture immediately
      await startCameraCapture();
    } catch (err) { 
      setErrorMsg("Error iniciando escáner: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsLoading(false);
    }
  };
  
  const startCameraCapture = () => {
    if (!scannerRef.current) {
      setErrorMsg("Error: Escáner no inicializado correctamente");
      return;
    }
    
    setIsLoading(true);
    setIsScanning(true);
    setPermissionDenied(false);
    
    const aspectRatio = window.innerWidth > 800 ? 1.777778 : window.innerWidth / window.innerHeight;
    const qrboxWidth = Math.min(250, window.innerWidth - 50);
    const qrboxHeight = Math.min(250, window.innerHeight - 100);

    // Camera config with reasonable defaults
    const config = {
      fps: 10,
      qrbox: { 
        width: qrboxWidth,
        height: qrboxHeight 
      },
      aspectRatio
    };

    scannerRef.current
      .start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          onScanSuccess(decodedText);
          stopScanner();
        },
        () => {
          // Silently ignore scan processing errors
        }
      )
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        
        const errString = err.toString();
        
        // Check for common permission errors
        if (
          errString.includes("NotAllowedError") || 
          errString.includes("permission") ||
          errString.includes("denied")
        ) {
          setPermissionDenied(true);
          onScanError("Se requiere permiso para acceder a la cámara. Por favor, acepta el permiso en tu navegador.");
        } else if (errString.includes("NotFoundError") || errString.includes("no camera")) {
          onScanError("No se encontró ninguna cámara en tu dispositivo.");
        } else if (errString.includes("IndexSizeError")) {
          onScanError("Error de tamaño: Por favor recarga la página e intenta nuevamente.");
        } else {
          onScanError("Error al iniciar la cámara: " + errString);
        }
        
        setIsScanning(false);
        setErrorMsg(errString);
      });
  };

  const stopScanner = async () => {
    // stop + full cleanup
    if (scannerRef.current?.isScanning) {
      await scannerRef.current.stop().catch(() => {});
    }
    await cleanupScanner().catch(() => {});
    setIsScanning(false);
    setErrorMsg(null);
  };
  
  const resetAndRetry = () => {
    // Full reset of scanner
    stopScanner();
    
    // Clear ref to force re-initialization
    if (scannerRef.current) {
      try {
        scannerRef.current.clear();
      } catch {
        // Silently handle clear errors
      }
      scannerRef.current = null;
    }
    
    setErrorMsg(null);
    setPermissionDenied(false);
    
    // Initialize scanner again after a brief delay
    setTimeout(() => {
      startScanner();
    }, 500);
  };

  return (
    <Space direction="vertical" style={{ 
      width: '100%', 
      padding: '0.5rem',
      minHeight: '60vh'
    }}>
      <Title level={4}>Escanear código QR</Title>
      
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16 }}>
            Inicializando cámara...
          </Text>
        </div>
      )}
      
      {permissionDenied && (
        <Alert
          message="Permiso de cámara denegado"
          description="Para escanear códigos QR, debes permitir el acceso a la cámara en tu navegador."
          type="error"
          showIcon
        />
      )}
      
      {errorMsg && !isLoading && (
        <Alert
          message="Error en el escáner"
          description={errorMsg}
          type="error"
          showIcon
          action={
            <Button size="small" danger onClick={resetAndRetry}>
              Reintentar
            </Button>
          }
        />
      )}
      
      <div 
        ref={containerRef} 
        style={{ 
          width: '100%', 
          minHeight: '300px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px dashed #ccc',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
        }}
      />
      
      <Space>
        {!isScanning ? (
          <Button type="primary" onClick={startScanner} loading={isLoading}>
            Iniciar escáner
          </Button>
        ) : (
          <Button danger onClick={stopScanner} disabled={isLoading}>
            Detener escáner
          </Button>
        )}
        
        {onCancel && (
          <Button onClick={() => {
            stopScanner();
            onCancel();
          }} disabled={isLoading}>
            Cancelar
          </Button>
        )}
        
        {!isLoading && isScanning && (
          <Button onClick={resetAndRetry}>
            Reiniciar cámara
          </Button>
        )}
      </Space>
      
      <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
        Apunta la cámara hacia un código QR válido. Asegúrate de tener buena iluminación.
      </Text>
    </Space>
  );
};

export default QrScanner;
